import React from "react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Link,
    TableCell,
    Typography,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import TableRow from "@mui/material/TableRow";
import {addFiltersToTable} from "../../../../../components/CatalogFilters/catalog_filters";
import EventModal from "../../../../messages/event_modal";
import DownloadIcon from "@mui/icons-material/Attachment";
import {HighlightOff} from "@mui/icons-material";
import ContentLoader from "../../../../../components/content_loader";
import CatalogCheckbox from "../../../../../components/EnhancedCheckbox";
import CatalogManagerResults, { ContentWithTooltip as ContentWithTooltipUntyped } from "../../../../../components/CatalogManagerResults";
import { tableRowSx, tableCellNoBorderSx, defaultResultsContainerStyles, DialogHeader } from "../../../../../theme";

// TODO(ENG-344): CatalogManagerResults is still .js — drop this cast once it's typed.
const ContentWithTooltip: any = ContentWithTooltipUntyped;

// region TypeScript Interfaces
interface EventFile {
    name: string;
    content: string;
}

interface EventNoticeDialogProps {
    style?: React.CSSProperties;
    loading: boolean;
    showEvent: boolean;
    eventDetails: string;
    files?: EventFile[];
    open: boolean;
    setOpen: (open: boolean) => void;
}

interface CatalogEventResultsProps {
    table: any;
    config: any;
    setConfig: (fn: (prevState: any) => any) => void;
    forceRefresh?: any;
    condensed?: boolean;
    tableHeader?: React.ReactNode;
    isSelectable?: boolean;
    bulkActions?: React.ReactNode;
    disableColumnSelector?: boolean;
    nonCondensedRows?: boolean;
    buttonFilters?: any;
    noToolBar?: boolean;
    inLineFilters?: any;
    showSavedSearch?: boolean;
    savedSearchTableType?: string;
    disableOnViewAction?: boolean;
    // Some call sites pass route-style props through (history, location,
    // currentCatalog) that aren't read internally. Accept them as extras.
    history?: any;
    location?: any;
    currentCatalog?: string;
}
// endregion

const StyledTooltipContent = styled('div')(({ theme }) => ({
    padding: '4px 8px',
    fontSize: '12px',
    fontFamily: theme.typography.fontFamily,
    color: 'rgba(0, 0, 0, 0.87)',
    backgroundColor: '#ffffff',
    borderRadius: '2px'
}));

const StyledTooltipArrow = styled('div')({
    color: '#ffffff',
    '&::before': {
        border: '1px solid rgba(0, 0, 0, 0.08)',
        backgroundColor: '#ffffff',
        boxShadow: '1px 1px 3px rgba(0, 0, 0, 0.08)'
    }
});

const StyledTooltip = styled('div')({
    backgroundColor: '#ffffff',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
    margin: 0,
    '& .MuiTooltip-arrow': {
        color: '#ffffff',
        fontSize: '12px'
    }
});

const EventNoticeDialog = (props: EventNoticeDialogProps) => {
    const {style, loading, showEvent, eventDetails, files} = props;
    const {open, setOpen} = props;

    return (
        <Dialog open={open} fullWidth={true} maxWidth='md' onClose={() => setOpen(false)}
                slotProps={{
                    paper: {
                        style: {
                            borderRadius: "12px"
                        }
                    }
                }}
        >
            <DialogTitle>
                <Grid item container justifyContent={"space-between"} alignItems={"center"}>
                    <Grid item container style={{flex: 0.5, flexDirection: "column"}}>
                        <DialogHeader>
                            Event Details
                        </DialogHeader>
                    </Grid>

                    <IconButton onClick={() => setOpen(false)} size="large" aria-label="Close">
                        <HighlightOff style={{color: "red"}}/>
                    </IconButton>
                </Grid>
            </DialogTitle>
            <DialogContent>
                {(showEvent && !loading) ? (
                    <>
                        <Typography className="eventModalDetail" style={style}
                                    dangerouslySetInnerHTML={{__html: eventDetails}}></Typography>

                        {files && files.length > 0 &&
                            <div>
                                {files.map(r =>
                                    <Typography key={r.name}>
                                        <Link color='primary' component="a"
                                              style={{display: 'flex'}} download={r.name}
                                              href={'data:application/octet-stream;base64,' + r.content}>
                                            <DownloadIcon/>{r.name}
                                        </Link>
                                    </Typography>
                                )}
                            </div>
                        }
                    </>
                ) : (
                    <ContentLoader preserveAspectRatio='none'
                                   style={{height: '6em', width: '100%'}}
                                   primaryColor='#e1e1e1'
                                   secondaryColor='#d8d8d8'/>
                )}
            </DialogContent>
        </Dialog>
    );
}


function CatalogEventResults(props: CatalogEventResultsProps) {
    let table = props.table;
    const [eventId, setEventId] = React.useState<any>(null);
    const [eventViewed, setEventViewed] = React.useState(false);
    const [eventDialogOpen, setEventDialogOpen] = React.useState(false);

    // Styled component classes object for ContentWithTooltip compatibility
    const classes = {
        tooltipContent: StyledTooltipContent,
        tooltipArrow: StyledTooltipArrow,
        tooltip: StyledTooltip
    };

    // Format CatalogManagerTable Data Into Rows //
    const generateRows = () => {
        return table.data.map((row: any, rowIndex: number) => {
            const rowId = row[props.config.keyField] ?? row.Id;
            return (
                <TableRow key={rowIndex} sx={tableRowSx} style={{height: "70px"}}>
                    {props.isSelectable && (
                        <TableCell padding="checkbox" style={{width: '3em'}}>
                            <CatalogCheckbox
                                checked={props.table.extensions.isSelected(rowId)}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.table.extensions.handleClick(e, rowId)}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            />
                        </TableCell>
                    )}
                    {props.config.columns.map((column: any, colIndex: number) => {
                        if (props.table.extensions.isColumnHidden(column)) return null;
                        if (column.hidden && !column.toggleable) return null;

                        let cellContent = column.template
                            ? column.template(row, column, colIndex, rowIndex,
                                (filter: any) => addFiltersToTable([filter], props.table),
                                setEventId, setEventViewed, setEventDialogOpen)
                            : row[column.id];

                        let content = (
                            <TableCell
                                key={`${column.id}-${rowIndex}`}
                                style={{
                                    width: column.width,
                                    maxWidth: column.width,
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                    padding: !props.isSelectable
                                    ? (props.nonCondensedRows ? "4px 0px 4px 24px" : undefined)
                                    : "0 6px 0 0",
                                    ...column.cellStyle,
                                }}
                            >
                                <ContentWithTooltip
                                    cellContent={cellContent}
                                    column={column}
                                    row={row}
                                    colIndex={colIndex}
                                    rowIndex={rowIndex}
                                    classes={classes}
                                />
                            </TableCell>
                        );

                        return <React.Fragment key={`${rowIndex}-${colIndex}`}>{content}</React.Fragment>
                    })}

                    <TableCell sx={tableCellNoBorderSx} style={{width: 0, padding: "4px 0"}}
                               onClick={(e) => {
                                   e.stopPropagation();
                                   e.preventDefault();
                               }}
                    >
                        <Grid container style={{flexDirection: "column", margin: "0"}}>
                        </Grid>
                    </TableCell>
                </TableRow>
            )
        })
    };

    return (
        <>
            <CatalogManagerResults
                table={table}
                config={props.config}
                setConfig={props.setConfig}
                generateRows={generateRows}
                forceRefresh={props.forceRefresh}
                condensed={props.condensed || false}
                tableHeader={props.tableHeader}
                resultsContainerStyles={defaultResultsContainerStyles}
                disableSearch={true}
                disableColumnSelector={props.disableColumnSelector ?? true}
                buttonFilters={props.buttonFilters}
                noToolBar={props.noToolBar}
                inLineFilters={props.inLineFilters}
                showSavedSearch={props.showSavedSearch}
                savedSearchTableType={props.savedSearchTableType || "catalog-events"}
                hideTopPagination={true}
                isSelectable={props.isSelectable}
                bulkActions={props.bulkActions}
            />

            <EventModal eventId={eventId} viewed={eventViewed}
                        onView={() => {
                            if(!props.disableOnViewAction) {
                                props.setConfig(prevState => ({...prevState, refresh: !prevState.refresh}))
                            }
                        }}
                        template={(props: any) => <EventNoticeDialog {...props} open={eventDialogOpen}
                                                                setOpen={setEventDialogOpen}/>}
            />
        </>
    )
}

export default CatalogEventResults;
